import { ForwardedRef, forwardRef, JSX, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import Webcam from 'react-webcam'
import { Box } from '@chakra-ui/react'

export type CamerRefHandle = {
  changeCamera: () => void
  capture: () => Promise<CapturedImage | undefined>
  captureWithFlash: () => Promise<CapturedImage | undefined>
}

export type CapturedImage = {
  imageSrc: string
  imageBlob: Blob
}

// eslint-disable-next-line @typescript-eslint/ban-types
type Props = {}

export default forwardRef<CamerRefHandle, Props>(function Camera(
  props: Props,
  ref: ForwardedRef<CamerRefHandle>,
): JSX.Element {
  const webcamRef = useRef<Webcam>(null)

  const [currentFacingMode, setCurrentFacingMode] = useState<'user' | { exact: 'environment' }>({
    exact: 'environment',
  })

  const imageCaptureRef = useRef<any | null>(null) // ImageCapture isn't in all TS lib.dom versions, ensure build with any

  useImperativeHandle(ref, () => ({
    changeCamera: changeVideoConstraints,
    capture,
    captureWithFlash,
  }))

  const base64toBlob = async (base64Data: string) => {
    const base64Response = await fetch(base64Data)
    const blob = await base64Response.blob()
    return blob
  }

  const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })

  const changeVideoConstraints = () => {
    if (currentFacingMode !== 'user') {
      setCurrentFacingMode('user')
    } else {
      setCurrentFacingMode({ exact: 'environment' })
    }
  }

  const capture = useCallback(async (): Promise<CapturedImage | undefined> => {
    const imageSrc = webcamRef.current?.getScreenshot()
    if (imageSrc) {
      const blob = await base64toBlob(imageSrc)
      return { imageSrc, imageBlob: blob }
    }
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamRef])

  const captureWithFlash = useCallback(async (): Promise<CapturedImage | undefined> => {
    const ic = imageCaptureRef.current
    if (!ic) {
      return await capture()
    }

    try {
      const blob: Blob = await ic.takePhoto({ fillLightMode: 'flash' })
      const imageSrc = await blobToDataUrl(blob)
      return { imageSrc, imageBlob: blob }
    } catch {
      // If fails, use normal capture
      try {
        const blob: Blob = await ic.takePhoto()
        const imageSrc = await blobToDataUrl(blob)
        return { imageSrc, imageBlob: blob }
      } catch {
        // Last resort, old function
        return await capture()
      }
    }
  }, [capture])

  const handleUserMedia = useCallback(async () => {
    const stream: MediaStream | undefined = (webcamRef.current as unknown as { stream?: MediaStream })?.stream
    if (!stream) return

    const track = stream.getVideoTracks()[0]

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ic = new (window as any).ImageCapture(track)
      imageCaptureRef.current = ic
    } catch {
      imageCaptureRef.current = null
    }
  }, [])

  const handleUserMediaError = useCallback(() => {
    imageCaptureRef.current = null
  }, [])

  useEffect(() => {
    imageCaptureRef.current = null
  }, [currentFacingMode])

  return (
    <Box display="flex" justifyContent="center" height={'100%'} width={'100%'}>
      <Webcam
        audio={false}
        ref={webcamRef}
        screenshotFormat="image/jpeg"
        screenshotQuality={1}
        forceScreenshotSourceSize
        videoConstraints={{ facingMode: currentFacingMode, width: 1080 }}
        style={{ height: '100%', objectFit: 'scale-down' }}
        onUserMedia={handleUserMedia}
        onUserMediaError={handleUserMediaError}
      />
    </Box>
  )
})
